
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { translations } from '@/utils/translations';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import EnhancedPageTransition from '@/components/EnhancedPageTransition';
import BlogPostContent from '@/components/BlogPostContent';

interface BlogPostTemplateProps {
  title: string;
  date: string;
  image: string;
  content: string;
  description?: string;
  slug: string;
}

const BlogPostTemplate: React.FC<BlogPostTemplateProps> = ({
  title,
  date,
  image,
  content,
  description,
  slug
}) => {
  const { currentLanguage, getLocalizedPath } = useLanguage();
  const t = translations[currentLanguage];
  const [imageError, setImageError] = React.useState(false);
  
  // Default fallback image if needed
  const fallbackImage = "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?q=80&w=1200&auto=format&fit=crop";
  
  // Function to handle fallback image if main image fails
  const handleImageError = () => {
    console.error("Image failed to load:", image);
    setImageError(true);
  };

  // Animation variants for content
  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.7 } }
  };
  
  return (
    <EnhancedPageTransition variant="fade" duration={0.4}>
      <div className="flex flex-col min-h-screen bg-[#121212]">
        <Header />
        <main className="flex-grow py-16 bg-gradient-to-r from-[#1A1F2C] to-[#2D3748]" itemScope itemType="https://schema.org/Article">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.nav 
              className="mb-6" 
              aria-label="Breadcrumb"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <ol className="flex items-center space-x-2 text-sm flex-wrap">
                <li>
                  <Link to={getLocalizedPath('/')} className="text-purple-400 hover:underline">
                    {t.home}
                  </Link>
                </li>
                <li>
                  <span className="text-gray-400 mx-2">/</span>
                  <Link to={getLocalizedPath('blog')} className="text-purple-400 hover:underline">
                    {t.blog}
                  </Link>
                </li>
                <li>
                  <span className="text-gray-400 mx-2">/</span>
                  <span className="text-gray-300" itemProp="headline">{title}</span>
                </li>
              </ol>
            </motion.nav>
            
            <div className="max-w-4xl mx-auto">
              <motion.div 
                className="w-full h-72 md:h-96 rounded-xl mb-8 shadow-lg overflow-hidden"
                variants={contentVariants}
                initial="initial"
                animate="animate"
              >
                <img 
                  src={imageError ? fallbackImage : image || fallbackImage}
                  alt={title || "Blog post featured image"}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  itemProp="image"
                  onError={handleImageError}
                  loading="eager"
                  width="800"
                  height="450"
                />
              </motion.div>
              
              <motion.div 
                className="glass-card rounded-xl p-6 md:p-8 shadow-lg mb-6"
                variants={contentVariants}
                initial="initial"
                animate="animate"
                transition={{ delay: 0.2 }}
              >
                <time dateTime={date} className="text-sm text-purple-300" itemProp="datePublished">
                  {date}
                </time>
                
                <motion.h1 
                  className="text-3xl md:text-4xl font-bold mt-2 mb-6 text-white font-future bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-400" 
                  itemProp="headline"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  {title}
                </motion.h1>
                
                {description && (
                  <motion.p
                    className="text-lg mb-6 text-gray-300"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                  >
                    {description}
                  </motion.p>
                )}
                
                <BlogPostContent 
                  htmlContent={content} 
                  languageCode={currentLanguage} 
                />
              </motion.div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </EnhancedPageTransition>
  );
};

export default BlogPostTemplate;
